import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Modal,
  Form,
  Input,
  Switch,
  Alert,
  Space,
  Row,
  Col,
  message,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import {
  resizeImageTo400x400,
  uploadCategoryImage,
} from '../services/uploadService';
import { PageHeader, DataTable, LoadingSkeleton } from '../design-system';

const { TextArea } = Input;

const CATEGORY_IMAGE_SIZE = 400;

interface Category {
  id: number;
  name: string;
  description: string;
  image_url?: string;
  imageUrl?: string; // API có thể trả camelCase
  is_active: boolean;
  is_popular: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export default function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form] = Form.useForm();
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (modalVisible && editingCategory) {
      const imageUrl =
        editingCategory.image_url ?? editingCategory.imageUrl ?? undefined;
      form.setFieldsValue({
        name: editingCategory.name,
        description: editingCategory.description,
        image_url: imageUrl,
        is_active: editingCategory.is_active,
        is_popular: editingCategory.is_popular,
        sort_order: editingCategory.sort_order,
      });
    }
  }, [modalVisible, editingCategory, form]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/categories');
      setCategories(response.data.data);
      setError(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message || 'Không thể tải danh sách danh mục'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category?: Category) => {
    setEditingCategory(category ?? null);
    if (!category) form.resetFields();
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingCategory(null);
    setUploadingImage(false);
    form.resetFields();
  };

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('Chỉ chấp nhận file hình ảnh (jpg, png, gif, webp)');
      return;
    }
    try {
      setUploadingImage(true);
      const resized = await resizeImageTo400x400(file);
      const url = await uploadCategoryImage(resized);
      form.setFieldsValue({ image_url: url });
      message.success('Upload ảnh 400x400 thành công');
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error(
        e.response?.data?.message || e.message || 'Upload ảnh thất bại'
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (values: {
    name: string;
    description: string;
    image_url?: string;
    is_active: boolean;
    is_popular: boolean;
    sort_order: number;
  }) => {
    try {
      if (editingCategory) {
        await api.put(`/admin/categories/${editingCategory.id}`, values);
        message.success('Cập nhật danh mục thành công');
      } else {
        await api.post('/admin/categories', values);
        message.success('Thêm danh mục thành công');
      }
      handleCloseModal();
      loadCategories();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      message.error(error.response?.data?.message || 'Không thể lưu danh mục');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/admin/categories/${id}`);
      message.success('Xóa danh mục thành công');
      loadCategories();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      message.error(error.response?.data?.message || 'Không thể xóa danh mục');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await api.put(`/admin/categories/${id}/toggle-status`);
      message.success('Cập nhật trạng thái thành công');
      loadCategories();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      message.error(
        error.response?.data?.message || 'Không thể thay đổi trạng thái'
      );
    }
  };

  const columns = [
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => description || '-',
    },
    {
      title: 'Thứ tự',
      dataIndex: 'sort_order',
      key: 'sort_order',
    },
    {
      title: 'Phổ biến',
      dataIndex: 'is_popular',
      key: 'is_popular',
      render: (isPopular: boolean) => (
        <span style={{ color: isPopular ? '#52c41a' : '#999' }}>
          {isPopular ? 'Có' : 'Không'}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean, record: Category) => (
        <Button
          type="text"
          icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          onClick={() => handleToggleStatus(record.id)}
        >
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Button>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: unknown, record: Category) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            Sửa
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Lỗi"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={loadCategories}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Quản lý danh mục món ăn"
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            Thêm danh mục
          </Button>
        }
      />

      {error && (
        <Alert
          message="Lỗi"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
      >
        {loading ? (
          <LoadingSkeleton rows={5} showCard={false} />
        ) : (
          <DataTable
            columns={columns}
            dataSource={categories}
            rowKey="id"
            loading={loading}
            pagination={{
              current: 1,
              total: categories.length,
              pageSize: 10,
              onChange: () => {
                // Do nothing
              },
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} danh mục`,
            }}
          />
        )}
      </Card>

      <Modal
        title={editingCategory ? 'Sửa danh mục' : 'Thêm danh mục'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
        destroyOnClose={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            is_popular: false,
            sort_order: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tên danh mục"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên danh mục' },
                ]}
              >
                <Input placeholder="Nhập tên danh mục" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sort_order" label="Thứ tự">
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Mô tả">
            <TextArea rows={3} placeholder="Nhập mô tả danh mục" />
          </Form.Item>

          <Form.Item name="image_url" label="Hình ảnh danh mục (400x400)">
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const imageUrl = getFieldValue('image_url');
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 16,
                    }}
                  >
                    {imageUrl ? (
                      <div style={{ flexShrink: 0, position: 'relative' }}>
                        <img
                          src={imageUrl}
                          alt="Category"
                          style={{
                            width: CATEGORY_IMAGE_SIZE,
                            height: CATEGORY_IMAGE_SIZE,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #d9d9d9',
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: CATEGORY_IMAGE_SIZE,
                          height: CATEGORY_IMAGE_SIZE,
                          border: '1px dashed #d9d9d9',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fafafa',
                        }}
                      >
                        <PictureOutlined
                          style={{ fontSize: 48, color: '#bfbfbf' }}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <Upload
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        showUploadList={false}
                        beforeUpload={f => {
                          handleImageUpload(f);
                          return false;
                        }}
                        disabled={uploadingImage}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          loading={uploadingImage}
                        >
                          {imageUrl ? 'Thay ảnh' : 'Chọn ảnh (400x400)'}
                        </Button>
                      </Upload>
                      {imageUrl && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() =>
                            form.setFieldsValue({ image_url: undefined })
                          }
                        >
                          Xóa ảnh
                        </Button>
                      )}
                    </div>
                  </div>
                );
              }}
            </Form.Item>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Hoạt động"
                  unCheckedChildren="Không hoạt động"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_popular"
                label="Phổ biến"
                valuePropName="checked"
              >
                <Switch checkedChildren="Có" unCheckedChildren="Không" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCategory ? 'Cập nhật' : 'Thêm'}
              </Button>
              <Button onClick={handleCloseModal}>Hủy</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
